import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { IMPACT_STATS } from "@/content";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1600;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {n.toLocaleString("gu-IN")}
      {suffix}
    </span>
  );
};

export default function Stats() {
  return (
    <section id="about" data-testid="stats-section" className="py-20 lg:py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="font-head font-bold text-sm tracking-[0.2em] text-terracotta mb-4">
              અમારા વિશે
            </p>
            <h2 className="font-display text-ink text-4xl sm:text-5xl leading-snug">
              દરેક દિવ્યાંગ સુધી
              <span className="text-leaf"> પ્રેમ અને સહાય</span>
            </h2>
            <p className="mt-6 text-ink/70 text-base md:text-lg leading-relaxed">
              છેલ્લા ૧૭ વર્ષથી શ્રી યુનિક વિકલાંગ ચેરિટેબલ ટ્રસ્ટ રાજકોટમાં
              શારીરિક, માનસિક, સેરેબ્રલ પાલ્સી, અંધ, મૂક-બધિર સહિત ૨૧
              કેટેગરીના દિવ્યાંગ બંધુઓના શૈક્ષણિક, આર્થિક, સામાજિક અને માનસિક
              વિકાસ માટે કાર્યરત છે.
            </p>
            <p className="mt-4 text-ink/70 text-base md:text-lg leading-relaxed">
              રમતગમત વિભાગ દ્વારા પ્રમાણિત અને પેરાલિમ્પિક કમિટી ઓફ ગુજરાત
              સાથે સંકળાયેલી અમારી સંસ્થા રાજકોટ અને મોરબી જિલ્લાના ખેલ
              મહાકુંભની સંકલનકર્તા છે. 'પ્રાઈડ ઓફ રાજકોટ' એવોર્ડથી સન્માનિત
              ટ્રસ્ટના ખેલાડીઓએ રાષ્ટ્રીય અને આંતરરાષ્ટ્રીય સ્તરે અનેક મેડલ
              જીત્યા છે.
            </p>
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4 sm:gap-5">
            {IMPACT_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                data-testid={`stat-card-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`rounded-2xl p-7 sm:p-9 border border-stone-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-200 ${
                  i % 3 === 0 ? "bg-leaf-soft" : i % 3 === 1 ? "bg-saffron-soft" : "bg-white"
                } ${i === 0 ? "sm:translate-y-6" : ""}`}
              >
                <p className="font-display text-3xl sm:text-4xl text-ink">
                  <Counter value={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-2 font-head font-semibold text-sm sm:text-base text-ink/60">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
